import React from 'react';
import { FuelIcon, FuelReload } from '@frete.com/fuel-react';
import {
  formatDateToPatternRequested,
  formatHour,
} from '@utils/formatters/formatters';
import { hasOccurence } from '@utils/occurrence';
import { FuelFlex } from '@frete.com/fuel-station-react';
import Pulse, { ColorType } from '@components/ui/PulseV2';
import Skeleton from './Skeleton';
import { LastLocationPropsInterface } from './LastLocationTypes';
import { text } from '../text';
import * as Styled from './LastLocationStyles';

function LastLocation({
  isDone,
  isLoading,
  location,
  occurrence,
  changedStatusAt,
  onClickReload,
}: LastLocationPropsInterface) {
  const { cityName, stateAcronym, updatedAt } = location;
  const hasOccurrence = occurrence && hasOccurence(occurrence);

  const changedStatusAtDate = changedStatusAt
    ? new Date(changedStatusAt).toISOString()
    : new Date().toISOString();

  const updatedAtDate = updatedAt
    ? new Date(updatedAt).toISOString()
    : new Date().toISOString();

  function getLocationData(): {
    name: string;
    date: string;
    iconColor: ColorType;
  } {
    if (isDone) {
      return {
        iconColor: 'white',
        name: text.location.done,
        date: updatedAtDate,
      };
    }

    if (!cityName) {
      return {
        iconColor: 'gray',
        name: text.location.noCity,
        date: updatedAtDate,
      };
    }

    if (hasOccurrence) {
      return {
        iconColor: 'orange',
        name: `${cityName}, ${stateAcronym}`,
        date: updatedAtDate,
      };
    }

    return {
      iconColor: 'green',
      name: `${cityName}, ${stateAcronym}`,
      date: updatedAtDate,
    };
  }

  function renderLastLocation() {
    if (isLoading) {
      return <Skeleton />;
    }

    const { name, date, iconColor } = getLocationData();

    return (
      <>
        <FuelFlex defaultProps={{ gap: 'km2' }}>
          <Pulse
            label={{
              size: 'sm',
              text: name,
              color: 'neutral-strong',
              weight: 'bold',
            }}
            description={formatDateToPatternRequested(
              date,
              "'ÁS' HH:mm 'EM' dd MMM",
            )}
            icon={{ isPulse: true, color: iconColor }}
          />
          {hasOccurrence && (
            <Styled.WarningIconWrapper>
              <FuelIcon
                size="tiny"
                name="alert"
                iconStyle="filled"
                data-testid="journey-has-unread-occurence"
              />
            </Styled.WarningIconWrapper>
          )}
        </FuelFlex>
        <Styled.ReloadWrapper>
          <FuelReload
            onClick={() => onClickReload()}
            label={formatHour(changedStatusAtDate)}
            loading={isLoading}
          />
        </Styled.ReloadWrapper>
      </>
    );
  }

  return (
    <Styled.Section>
      <FuelFlex
        defaultProps={{
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 'km2',
        }}
      >
        {renderLastLocation()}
      </FuelFlex>
    </Styled.Section>
  );
}

export default LastLocation;
